import { ToastContainer, toast } from "react-toastify";
import Logo from "../../beatai-logo.svg";
import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Navbar,
  Dropdown,
  Spinner,
  Badge,
  Image,
  Nav,
  Card,
  ProgressBar,
  InputGroup,
  Modal,
} from "react-bootstrap";
import "./Subscription.css";
import "../chat/Chat.css";
import { useMediaQuery } from "react-responsive";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faBars,
  faCheckCircle,
  faMagicWandSparkles,
  faPerson,
  faSave,
  faShieldAlt,
  faSignOutAlt,
  faUserEdit,
  faLock,
  faShieldHalved,
  faKey,
  faUserSecret,
  faTriangleExclamation,
  faArrowCircleLeft,
  faIdBadge,
  faCoins,
  faHistory,
  faStar,
  faCreditCard,
  faPlus,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory } from "react-router-dom";
import Avatar, { useAvatar } from "./Avatar";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import {
  changeAvatar,
  changePassword,
  requestEmailChange,
  updateProfile,
  validateEmailChange,
} from "../../service/api";
import { useUserProfile } from "../../service/hooks";
import "react-toastify/dist/ReactToastify.min.css";
import CookiePolicyFooter from "../chat/Cookies";
import { activateSubscription, cancelSubscription, topupCredits, getTransactionHistory } from "../../service/workflow-api";

// Pricing data constants
const pricingData = {
  proMonthly: 13.99,
  proAnnual: 153.89,  // ~25/month - save 1 month
  platinumMonthly: 39.99,
  platinumAnnual: 439.89  // ~41.67/month - save 1 month
};

const Subscription = () => {
  const [activeTab, setActiveTab] = useState("Membership");
  const [billingCycle, setBillingCycle] = useState("annual");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(() => {
    try {
      const saved = localStorage.getItem('current_plan');
      return saved ? JSON.parse(saved) : { plan_id: 1, text: 'free' };
    } catch (e) {
      // Handle existing non-JSON data in localStorage
      const savedText = localStorage.getItem('current_plan');
      const defaultPlan = { plan_id: 1, text: 'free' };
      
      if (savedText) {
        // Convert old format to new format
        switch(savedText) {
          case 'pro':
            return { plan_id: 2, text: 'pro' };
          case 'platinum':
            return { plan_id: 3, text: 'platinum' };
          default:
            return defaultPlan;
        }
      }
      return defaultPlan;
    }
  });
  const [userCredits, setUserCredits] = useState({
    available: 0,
    used: 0,
    total: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState(false);

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Add a ref for the FAQ section
  const faqSectionRef = useRef(null);

  // Function to scroll to FAQ section
  const scrollToFaq = () => {
    if (faqSectionRef.current) {
      faqSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchTransactionHistory();
  }, []);

  const fetchTransactionHistory = async () => {
    try {
      const response = await getTransactionHistory();
      if (response.current_plan) {
        setCurrentPlan({
          plan_id: response.current_plan.id,
          text: response.current_plan.name.toLowerCase().replace(' plan', '')
        });
        setUserCredits({
          available: response.current_plan.credits_available,
          used: response.current_plan.credits_used,
          total: response.current_plan.credits_available
        });
      }
      setTransactions(response.transactions || []);
    } catch (error) {
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount(null);
  };

  const handleUpgrade = async (planName, planId) => {
    if (isProcessing) return;
    
    // Prevent subscribing to the same plan
    if (currentPlan.text === planName) {
      toast.info('You are already subscribed to this plan');
      return;
    }

    try {
      setIsProcessing(true);
      
      // If user has an active plan that's not free, prompt for confirmation
      if (currentPlan.text !== 'free') {
        setModalConfig({
          title: 'Confirm Plan Change',
          message: 'You need to cancel your current plan before upgrading. Would you like to proceed?',
          onConfirm: async () => {
            try {
              // Cancel current subscription first
              await cancelSubscription();
              toast.success('Current plan cancelled successfully');
              
              // Continue with upgrade
              completeUpgrade(planName, planId);
            } catch (error) {
              toast.error(error.message || 'Failed to cancel current plan');
              setIsProcessing(false);
            }
          },
          confirmText: 'Yes, Proceed',
          cancelText: 'No, Keep Current Plan'
        });
        setShowConfirmModal(true);
        setIsProcessing(false);
        return;
      }
      
      // Direct upgrade for free users
      await completeUpgrade(planName, planId);
    } catch (error) {
      toast.error(error.message || 'Failed to upgrade plan. Please try again.');
      setIsProcessing(false);
    }
  };

  // Helper function to complete the upgrade process
  const completeUpgrade = async (planName, planId) => {
    try {
      setIsProcessing(true);
      const response = await activateSubscription(planId, billingCycle);
      
      if (response.success) {
        const newPlan = {
          plan_id: planId,
          text: planName
        };
        localStorage.setItem('current_plan', JSON.stringify(newPlan));
        setCurrentPlan(newPlan);
        
        // Update credits from response
        setUserCredits(prev => ({
          ...prev,
          available: response.credits_balance,
          total: response.plan.credits
        }));
        
        toast.success(`Successfully upgraded to ${planName} plan!`);
        await fetchTransactionHistory(); // Refresh transaction history
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upgrade plan. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelPlan = async () => {
    if (isProcessing || currentPlan.text === 'free') return;
    
    setModalConfig({
      title: 'Cancel Subscription',
      message: 'Are you sure you want to cancel your current plan? You will be moved to the free plan.',
      onConfirm: async () => {
        try {
          setIsProcessing(true);
          const response = await cancelSubscription();
          
          if (response.success) {
            const newPlan = {
              plan_id: 1,
              text: 'free'
            };
            localStorage.setItem('current_plan', JSON.stringify(newPlan));
            setCurrentPlan(newPlan);
            
            // Update credits display
            setUserCredits(prev => ({
              ...prev,
              available: response.new_subscription.credits,
              total: response.new_subscription.credits
            }));
            
            toast.success('Successfully cancelled plan and moved to Free Plan');
            await fetchTransactionHistory(); // Refresh transaction history
          }
        } catch (error) {
          toast.error(error.message || 'Failed to cancel plan. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      },
      confirmText: 'Yes, Cancel Plan',
      cancelText: 'No, Keep My Plan'
    });
    setShowConfirmModal(true);
  };

  // Calculate displayed price based on billing cycle
  const getDisplayPrice = (plan) => {
    if (plan === "Free Plan") return 0.0;

    if (plan === "Pro Plan") {
      return billingCycle === "annual"
        ? pricingData.proAnnual
        : pricingData.proMonthly;
    }

    if (plan === "Platinum Plan") {
      return billingCycle === "annual"
        ? pricingData.platinumAnnual
        : pricingData.platinumMonthly;
    }

    return 0;
  };

  const handleTopupCredits = async () => {
    if (topupLoading) return;
    
    const amount = selectedAmount || parseFloat(customAmount);
    
    if (!amount || isNaN(amount) || amount <= 0) {
      toast.error('Please select a valid amount');
      return;
    }
    
    try {
      setTopupLoading(true);
      
      // Calculate credits based on amount
      // $1 = approximately 12 credits
      const creditsToAdd = Math.floor(amount * 12);
      
      const response = await topupCredits(creditsToAdd, amount);
      
      if (response.success) {
        setUserCredits(prev => ({
          ...prev,
          available: response.new_balance,
          total: prev.total + creditsToAdd
        }));
        
        toast.success(`Credits topped up successfully!`);
        await fetchTransactionHistory(); // Refresh transaction history
        
        // Reset selected amount
        setSelectedAmount(null);
        setCustomAmount("");
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add credits. Please try again.');
    } finally {
      setTopupLoading(false);
    }
  };

  const toggleChangePlan = () => {
    setShowChangePlan(!showChangePlan);
  };

  const history = useHistory();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const { firstname, lastname, email, refreshProfile } = useUserProfile();
  const [avatarB64Local, setAvatarB64Local] = useState(null);
  const [changingAvatar, setChangingAvatar] = useState(false);
  const uploadAvatarRef = useRef(null);

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a
      href=""
      style={{
        textDecoration: "none",
      }}
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </a>
  ));

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    history.push("/login?sc=ef5199c2-e6b8-4257-9932-20aa6f449c90R");
  };

  const handleFileChange = async (event) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    const file = event.target.files[0];

    // Validate file type (JPEG/PNG)
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      alert("Only JPEG and PNG formats are allowed.");
      return;
    }

    try {
      setChangingAvatar(true);
      await changeAvatar(file);
      alert("Avatar updated successfully!");

      // Fetch and update the base64 image in the hook state
      const avatar = await getAvatarB64(true);
      setAvatarB64Local(avatar);
      setChangingAvatar(false);
    } catch (error) {
      alert(error.message);
      setChangingAvatar(false);
    }
  };

  const { setAvatarB64, getAvatarB64 } = useAvatar();
  useEffect(() => {
    setChangingAvatar(true);

    getAvatarB64()
      .then((avatar) => {
        setChangingAvatar(false);
        setAvatarB64Local(avatar);
      })
      .catch((e) => {
        setChangingAvatar(false);
      });
  }, []);

  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const verified = localStorage.getItem("verified");

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Help icon that scrolls to FAQ */}
      <div className="help-icon" onClick={scrollToFaq}>
        <FontAwesomeIcon icon={faQuestionCircle} />
      </div>
      
      <Navbar bg="dark" variant="dark" expand="sm" className="w-100 m-0 nav_">
        <Container fluid className="">
          <input
            hidden
            ref={uploadAvatarRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileChange}
          />
          <Button
            variant="outline-light"
            onClick={handleShow}
            className="border-0"
            disabled
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>
          <Navbar.Brand
            className=""
            style={{ cursor: "pointer" }}
            title="+ Start new chat"
            onClick={(e) => history.push("/chat/")}
          >
            <Image src={Logo} style={{ maxWidth: "100px" }} />
          </Navbar.Brand>
          <Nav className="" style={{ width: "80%" }}>
            <Nav.Link
              as={Link}
              to="/chat/"
              className={
                window.location.href.indexOf("ai-image") > -1
                  ? "active nav-actv nav-link-style-cust nav-link_"
                  : "nav-link_ active"
              }
            >
              <FontAwesomeIcon
                style={{ color: "#d9d9d9", marginRight: "2px" }}
                icon={faArrowCircleLeft}
              />{" "}
              <span className="lex-color">
                <b> Lex</b>
              </span>
            </Nav.Link>
          </Nav>
          <Dropdown align="end" className="user-avatar-dropdown">
            <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
              <div className="user-avatar" style={{ textAlign: "center" }}>
                {!avatarB64Local &&
                  !changingAvatar &&
                  (firstname ? (
                    firstname[0]
                  ) : (
                    <FontAwesomeIcon icon={faPerson} />
                  ))}
                {!changingAvatar && avatarB64Local && (
                  <Avatar
                    base64Image={avatarB64Local}
                    width="30px"
                    height="30px"
                  />
                )}
                {changingAvatar && <Spinner size="sm" />}
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Container fluid>
                <Container className="text-center">
                  <div
                    className="user-avatar"
                    style={{ margin: "0 auto", width: "80px", height: "80px" }}
                    onClick={() => {
                      if (uploadAvatarRef.current) {
                        uploadAvatarRef.current.click();
                      }
                    }}
                  >
                    {!avatarB64Local &&
                      !changingAvatar &&
                      (firstname ? (
                        firstname[0]
                      ) : (
                        <FontAwesomeIcon icon={faPerson} />
                      ))}
                    {!changingAvatar && avatarB64Local && (
                      <Avatar
                        base64Image={avatarB64Local}
                        width="70px"
                        height="70px"
                      />
                    )}
                    {changingAvatar && <Spinner size="sm" />}
                  </div>
                  <br />
                  <p
                    style={{
                      fontSize: "0.7em",
                      color: "white",
                      margin: "0 auto",
                    }}
                  >
                    {firstname} {lastname}
                  </p>
                </Container>
              </Container>
              <Dropdown.Divider />
              <Dropdown.Item
                className="user-avt-dd-itm"
                onClick={() => {
                  if (uploadAvatarRef.current) {
                    uploadAvatarRef.current.click();
                  }
                }}
              >
                <FontAwesomeIcon icon={faUserEdit} className="me-2" /> Change
                Avatar
              </Dropdown.Item>
              <Dropdown.Item className="user-avt-dd-itm">
                <Link
                  to="/membership"
                  style={{
                    textDecoration: "none",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faIdBadge} className="me-2" />{" "}
                  Membership
                </Link>
              </Dropdown.Item>
              <Dropdown.Item className="user-avt-dd-itm" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Logout
              </Dropdown.Item>
              <Dropdown.Item className="user-avt-dd-itm">
                <Link
                  to="/account-settings"
                  style={{
                    textDecoration: "none",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faShieldAlt} className="me-2" />{" "}
                  Account Settings
                </Link>
              </Dropdown.Item>
              <Dropdown.Item className="user-avt-dd-itm" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {verified === "false" ? (
            <i className="ms-auto">
              <Badge>Unverified</Badge>
            </i>
          ) : null}
        </Container>
      </Navbar>

      <Container className="py-4 account-settings-page d-flex flex-column align-items-center mt-4">
        <Container
          className=""
          style={{
            maxWidth: !isTabletOrMobile ? "90%" : "95%",
            height: "100%",
          }}
        >
          {/* Top navigation tabs */}
          <div className="subscription-tabs">
            <div
              className={`subscription-tab ${
                activeTab === "Membership" ? "active" : ""
              }`}
              onClick={() => handleTabClick("Membership")}
            >
              <FontAwesomeIcon icon={faStar} className="me-2" /> Membership
            </div>
            <div
              className={`subscription-tab ${
                activeTab === "Top Up" ? "active" : ""
              }`}
              onClick={() => handleTabClick("Top Up")}
            >
              <FontAwesomeIcon icon={faCoins} className="me-2" /> Top Up
            </div>
            <div
              className={`subscription-tab ${
                activeTab === "History" ? "active" : ""
              }`}
              onClick={() => handleTabClick("History")}
            >
              <FontAwesomeIcon icon={faHistory} className="me-2" /> History
            </div>
          </div>
          <div className="subscription-content">
            {activeTab === "Membership" && (
              <div className="membership-content">
                {/* Current Plan Overlay - moved above pricing section */}
                <div className={`current-plan-overlay ${currentPlan.text}`}>
                  <div className="current-plan-content">
                    You're using <span className="plan-name">{currentPlan.text.charAt(0).toUpperCase() + currentPlan.text.slice(1)} Plan</span>
                  </div>
                </div>
                
                {/* Credits Usage Tracker */}
                <div className="credits-tracker mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="credits-label">Your credit usage</span>
                    <span className="credits-count">
                      {userCredits.used} / {userCredits.total}
                    </span>
                  </div>
                  <div className="credits-progress-container">
                    <div 
                      className="credits-progress-bar" 
                      style={{ 
                        width: `${(userCredits.used / userCredits.total) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <div className="credits-available mt-1">
                  </div>
                </div>

                {/* Change Plan Button - Only show when user has an active plan */}
                {currentPlan.text !== 'free' && !showChangePlan && (
                  <div className="text-center mb-4">
                    <h5 className="change-plan-title">Change Current Plan - Credits are Carry Forwarded</h5>
                    <Button 
                      className="make-payment-button" 
                      onClick={toggleChangePlan}
                    >
                      Change Plan
                    </Button>
                  </div>
                )}

                {/* Plan selection - Only show when change plan is clicked or no active plan */}
                {(showChangePlan || currentPlan.text === 'free') && (
                  <>
                    <div className="d-flex justify-content-end align-items-center mb-3">
                      {billingCycle === "annual" && (
                        <div className="me-3">
                          <span className="annual-savings">Save 1 month free</span>
                        </div>
                      )}
                      <div className="billing-toggle">
                        <div 
                          className={`billing-option ${billingCycle === "annual" ? "active" : ""}`}
                          onClick={() => handleBillingCycleChange("annual")}
                        >
                          Annual
                        </div>
                        <div 
                          className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}
                          onClick={() => handleBillingCycleChange("monthly")}
                        >
                          Monthly
                        </div>
                        <div className={`billing-slider ${billingCycle === "monthly" ? "monthly" : ""}`}></div>
                      </div>
                    </div>

                    <div className="membership-plans">
                      <div className="membership-plan">
                        <Badge className={currentPlan.text === 'free' ? "active-badge" : "d-none"}>Active</Badge>
                        <h3>Free Plan</h3>
                        <p>
                          <span className="price">Price: $0.00</span>
                        </p>
                        <p>Total Credits: 130 (One time)</p>
                        <p>Description: Limited credits, Restricted features</p>
                        <ul className="plan-features">
                          <li>Limited (only 10 images)</li>
                          <li>Maps Nav: No</li>
                          <li>News Search: No</li>
                          <li>Internet Use: No</li>
                          <li>Files Upload: No</li>
                          <li>Research Charts: No</li>
                          <li>Image & PDF Chat: No</li>
                        </ul>
                      </div>
                      <div className="membership-plan">
                        <Badge className={currentPlan.text === 'pro' ? "active-badge" : "d-none"}>Active</Badge>
                        <h3 className="pro-plan-text">Pro Plan</h3>
                        <p>
                          <span className="price">
                            {billingCycle === "annual" ? (
                              <>
                                <span className="original-price">${(pricingData.proMonthly * 12).toFixed(2)}</span>
                                ${pricingData.proAnnual.toFixed(2)}/year
                              </>
                            ) : (
                              `$${pricingData.proMonthly.toFixed(2)}/month`
                            )}
                          </span>
                        </p>
                        <p>
                          Total Credits: {billingCycle === "annual" 
                            ? `${1500 * 12}`
                            : 1500}
                        </p>
                        <Button 
                          className="w-100 make-payment-button"
                          onClick={() => handleUpgrade('pro', 2)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? <Spinner size="sm" /> : currentPlan.text === 'pro' ? 'Current Plan' : 'Upgrade Now'}
                        </Button>
                        <ul className="plan-features">
                          <li>
                            {billingCycle === "annual" 
                              ? "1,200 Images in total (100/mo × 12mo)" 
                              : "100 Images in total (max)"}
                          </li>
                          <li>Unlimited events search</li>
                          <li>AI Workflows</li>
                          <li>Maps Nav: Yes</li>
                          <li>News Search: Yes</li>
                          <li>Internet Use: Yes</li>
                          <li>Files Upload: Yes</li>
                          <li>Research Charts: Yes</li>
                          <li>Limited File Sizes</li>
                          <li>Image & PDF Chat: Yes</li>
                        </ul>
                      </div>
                      <div className="membership-plan">
                        <Badge className={currentPlan.text === 'platinum' ? "active-badge" : "d-none"}>Active</Badge>
                        <h3 className="platinum-plan-text">Platinum Plan</h3>
                        <p>
                          <span className="price">
                            {billingCycle === "annual" ? (
                              <>
                                <span className="original-price">${(pricingData.platinumMonthly * 12).toFixed(2)}</span>
                                ${pricingData.platinumAnnual.toFixed(2)}/year
                              </>
                            ) : (
                              `$${pricingData.platinumMonthly.toFixed(2)}/month`
                            )}
                          </span>
                        </p>
                        <p>
                          Total Credits: {billingCycle === "annual" 
                            ? `${4000 * 12}`
                            : 4000}
                        </p>
                        <Button 
                          className="w-100 make-payment-button"
                          onClick={() => handleUpgrade('platinum', 3)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? <Spinner size="sm" /> : currentPlan.text === 'platinum' ? 'Current Plan' : 'Upgrade Now'}
                        </Button>
                        <ul className="plan-features">
                          <li>
                            {billingCycle === "annual" 
                              ? "6,000 Images suggested (500/mo × 12mo)" 
                              : "500 Image suggested"}
                          </li>
                          <li>Unlimited events search</li>
                          <li>AI Workflows</li>
                          <li>Maps Nav: Yes</li>
                          <li>News Search: Yes</li>
                          <li>Internet Use: Yes</li>
                          <li>Files Upload: Yes</li>
                          <li>Research Charts: Yes</li>
                          <li>Large File Sizes</li>
                          <li>Image & PDF Chat: Yes</li>
                        </ul>
                      </div>
                    </div>

                    {/* Cancel Change Plan Button */}
                    {showChangePlan && (
                      <div className="text-center mt-4">
                        <Button 
                          className="make-payment-button" 
                          onClick={toggleChangePlan}
                          variant="secondary"
                        >
                          Cancel Plan Change
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Only show cancel plan button when showing plans and user has a non-free plan */}
                {currentPlan.text !== 'free' && showChangePlan && (
                  <div className="text-center mt-4">
                    <Button 
                      variant="outline-danger" 
                      onClick={handleCancelPlan}
                      disabled={isProcessing}
                      className="ms-3"
                    >
                      {isProcessing ? <Spinner size="sm" /> : 'Cancel Current Plan'}
                    </Button>
                  </div>
                )}
                
                {/* FAQ Section */}
                <div className="faq-section mt-5" ref={faqSectionRef}>
                  <h3 className="faq-title">
                    <FontAwesomeIcon icon={faCoins} className="me-2" /> 
                    Credits & Billing FAQ
                  </h3>
                  
                  <div className="faq-items">
                    <div className="faq-item glass-card">
                      <h4>How are credits deducted?</h4>
                      <p>Credits are deducted based on your AI usage:</p>
                      <ul>
                        <li>Different AI models consume varying amounts of credits per interaction</li>
                        <li>Premium models (xAI Grok & Claude 3.7 Sonnet) use more credits than general models</li>
                        <li>File uploads consume credits based on file size and complexity of processing</li>
                        <li>AI image generation and AI workflows also incur credit usage</li>
                      </ul>
                    </div>
                    
                    <div className="faq-item glass-card">
                      <h4>What happens to my unused credits?</h4>
                      <p>Your unused credits are rolled over month to month as long as you maintain an active subscription. If you cancel your plan, your remaining credits will stay in your account and can be used until they're depleted.</p>
                    </div>
                    
                    <div className="faq-item glass-card">
                      <h4>How do I manage my subscription?</h4>
                      <p>You can upgrade, downgrade, or cancel your subscription at any time from this membership page. Changes take effect immediately, with appropriate credits adjustments applied to your account.</p>
                    </div>
                    
                    <div className="faq-item glass-card">
                      <h4>Have questions or billing issues?</h4>
                      <p>For any disputes, questions about billing, or help with your subscription, please contact our support team at <a href="mailto:info@beatbind.io" className="support-email">info@beatbind.io</a></p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === "Top Up" && (
              <div className="topup-content">
                <h2>
                  <FontAwesomeIcon icon={faCoins} className="me-2" /> Add Credits
                  to Your Account
                </h2>

                <div className="topup-options">
                  <button
                    className={`topup-button ${
                      selectedAmount === 5 ? "selected" : ""
                    }`}
                    onClick={() => handleAmountSelect(5)}
                  >
                    <span className="amount">$5</span>
                    <span className="credits">+400 Credits</span>
                  </button>
                  <button
                    className={`topup-button ${
                      selectedAmount === 10 ? "selected" : ""
                    }`}
                    onClick={() => handleAmountSelect(10)}
                  >
                    <span className="amount">$10</span>
                    <span className="credits">+800 Credits</span>
                  </button>
                  <button
                    className={`topup-button ${
                      selectedAmount === 20 ? "selected" : ""
                    }`}
                    onClick={() => handleAmountSelect(20)}
                  >
                    <span className="amount">$20</span>
                    <span className="credits">+1600 Credits</span>
                  </button>
                  <button
                    className={`topup-button ${
                      selectedAmount === 50 ? "selected" : ""
                    }`}
                    onClick={() => handleAmountSelect(50)}
                  >
                    <span className="amount">$50</span>
                    <span className="credits">+4000 Credits</span>
                  </button>
                </div>

                <div className="custom-amount">
                  <label>Or enter a custom amount:</label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      className="custom-amount-input"
                      type="number"
                      min="1"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                    />
                  </InputGroup>
                  {customAmount && (
                    <small className="mt-2 text-light">
                      Approximately {Math.floor(parseFloat(customAmount) * 12)} credits
                    </small>
                  )}
                </div>

                <Button
                  className="make-payment-button"
                  disabled={(!selectedAmount && !customAmount) || topupLoading}
                  onClick={handleTopupCredits}
                >
                  {topupLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                      Make Payment (${selectedAmount || customAmount || 0})
                    </>
                  )}
                </Button>
              </div>
            )}
            {activeTab === "History" && (
              <div className="history-content">
                <h2>
                  <FontAwesomeIcon icon={faHistory} className="me-2" /> Transaction
                  History
                </h2>
                
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" variant="light" />
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="transaction-list">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="transaction-card glass-card mb-3">
                        <div className="transaction-card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1">
                                {transaction.type === 'subscription' ? (
                                  <>
                                    <FontAwesomeIcon icon={faStar} className="me-2" />
                                    {transaction.plan_name} ({transaction.billing_period})
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faCoins} className="me-2" />
                                    Credits Top-up
                                  </>
                                )}
                              </h6>
                              <small className="text-muted">
                                {new Date(transaction.date).toLocaleDateString()}
                              </small>
                            </div>
                            <div className="text-end">
                              <div className="transaction-amount">
                                ${transaction.amount_usd.toFixed(2)}
                              </div>
                              <div className="transaction-credits">
                                {transaction.credits_delta > 0 ? '+' : ''}{transaction.credits_delta} credits
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="glass-card bg-dark text-light mb-3">
                    <div className="card-body">
                      <p className="text-center">No transaction history available.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Container>
      </Container>
      <ToastContainer />

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop="static"
        keyboard={false}
        centered
        className="confirm-modal"
      >
        <Modal.Header closeButton className="confirm-modal-header">
          <Modal.Title>{modalConfig.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="confirm-modal-body">
          {modalConfig.message}
        </Modal.Body>
        <Modal.Footer className="confirm-modal-footer">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowConfirmModal(false)}
            className="modal-cancel-button"
          >
            {modalConfig.cancelText}
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowConfirmModal(false);
              modalConfig.onConfirm();
            }}
            className="modal-confirm-button"
          >
            {modalConfig.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subscription;